import React, { FC, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faPen } from '@fortawesome/free-solid-svg-icons';
import './ProviderTableInput.scss';
import { ButtonLoading } from '../ButtonLoading';
import clsx from 'clsx';

interface Props {
	initialValue: string | number | null;
	fieldName: string | null;
	inputType: string;
	isTableView?: boolean
	formik: any;
	onConfirm: (fieldName: string, value: string) => Promise<void>;
}

export const ProviderTableInput: FC<Props> = (props) => {
	const {
		initialValue,
		fieldName,
		inputType,
		isTableView,
		formik,
		onConfirm
	} = props;

	const getInitialValue = (): number | string => {
		return (initialValue === null || initialValue === undefined) ? null : initialValue;
	};

	const [isEditable, setIsEditable] = useState<boolean>(false);
	const [cellValue, setCellValue] = useState<string | number | null>(getInitialValue());
	const [loading, setLoading] = useState<boolean>(false);

	const inputValue = formik?.values?.[fieldName];

	const handleConfirm = async (): Promise<void> => {
		if (inputValue === cellValue) {
			setIsEditable(false);
			return;
		}
		try {

			if (formik.errors[fieldName]) return;

				setLoading(true);

				// Perform actual save
				await onConfirm(fieldName, inputValue);

				// Update local state
				setCellValue(inputValue);
				setIsEditable(false);
				formik.setErrors({});

		} catch (e) {
			console.error('Error in handleConfirm:', e);
		} finally {
			setLoading(false);
		}
	};

	const onClose = (): void => {
		setIsEditable(false);
		setCellValue(cellValue);
		formik.setFieldValue(fieldName, cellValue);
		formik.setErrors({});
	};

	const cellInput = (
		<div className="position-relative">
			<div className="d-flex">
				<div className={clsx(isTableView ? 'position-relative' : 'd-flex align-items-center')}>
					<div className={clsx('provider-input input-container')}>
						<input
							{...formik.getFieldProps(fieldName)}
							type={inputType}
							className= {clsx(
								'form-control form-control-sm form-control-solid',
								inputType === 'number' && 'number-input-zip',
								isTableView && 'w-100px'
							)}
							placeholder="Enter value"
							autoFocus
							data-cy='active-input'
						/>
					</div>
					<div
						className={clsx(
							'provider-icon-box',
							loading ? 'cursor-not-allowed' : 'cursor-pointer',
							isTableView && 'provider-save'
						)}
						onClick={(): Promise<void> => !loading && handleConfirm()}
						data-cy="save-button"
					>
						{
							loading
								? <ButtonLoading/>
								: <FontAwesomeIcon icon={faCheck}/>
						}

					</div>
					<div
						className={clsx(
							'provider-icon-box',
							loading ? 'cursor-not-allowed' : 'cursor-pointer',
							isTableView && 'provider-cancel'
						)}
						onClick={(): void => !loading && onClose()}
						data-cy="cancel-button"
					>
						<FontAwesomeIcon icon={faXmark}/>
					</div>

				</div>
			</div>
			{formik.touched[fieldName] && formik.errors[fieldName] && (
				<div className="text-danger fs-9 position-absolute" data-cy="error-text">
					{formik.errors[fieldName]}
				</div>
			)}
		</div>
	);

	const getCellValue = (): JSX.Element => {
		if (cellValue === null || cellValue === '') {
			return <span>-</span>;
		}

		return <span>{cellValue}</span>;
	};

	return (
		<>
			{
				isEditable
				? cellInput
				: (
					<div>
						<div
							className="fs-7 provider-cell cursor-pointer fc-text-overflow"
							onClick={(): void => setIsEditable(true)}
							data-cy="inactive-input"
						>
							<FontAwesomeIcon icon={faPen} className="me-2 w-10px text-danger"/>

							{getCellValue()}

						</div>
					</div>
				)
			}
		</>
	);
};
